'use client'

import { Card, Cell, Image, Spacer, Text } from '@vinted/web-ui'
import { useEffect } from 'react'

import { helpcenterLegacyHomeScreenView } from '@marketplace-web/domain/help-center-home-data'
import { HelpCenterTransactions } from '@marketplace-web/domain/help-center-transactions'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useLocation } from '@marketplace-web/shared/browser-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { useClientSideOnly } from '@marketplace-web/shared/ui-helpers'
import { useAsset } from '@marketplace-web/shared/assets'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'

import { generateFaqLink } from '_libs/utils/faq'

import { HelpCenterSearchInput } from '@marketplace-web/domain/help-center-faq-entry'
import useHelpCenterSessionTracking from '@marketplace-web/domain/help-center-session-tracking'

import HelpCenterNavigation from 'components/HelpCenterNavigation'

import { FaqEntryType } from 'constants/faq-entry'
import { HC_HOME_VISIBLE_TRANSACTION_COUNT } from 'constants/help-center'

import { getFaqEntries } from 'data/api/get-faq-entries'
import { getFaqEntryById } from 'data/api/get-faq-entry-by-id'
import { getRecentTransactions } from 'data/api/get-recent-transactions'
import { transformFaqEntryResponse } from 'data/api/transformers/response/transform-faq-entry-response'
import {
  transformHelpCenterHomeFaqsResponse,
  transformHelpCenterNavigationResponse,
  transformHelpCenterTransactionsResponse,
} from 'data/transformers/help-center'

import { HelpCenterHomeFaqEntryModel } from 'types/models/help-center'

const HelpCenterHome = () => {
  // Prevents translations rendering before spinner is shown
  const isClientSideRenderReady = useClientSideOnly()
  const translate = useTranslate('help_center')
  const { track } = useTracking()
  const { user, isWebview } = useSession()
  const isProductFeedbackFormEnabled = useFeatureSwitch('product_feedback_form')

  const asset = useAsset('/assets/icons/faq')
  const isLoggedIn = Boolean(user)

  const { access_channel, thread_id } = useLocation().searchParams
  const { helpCenterSessionId } = useHelpCenterSessionTracking()

  useEffect(() => {
    if (!helpCenterSessionId) return

    track(
      helpcenterLegacyHomeScreenView({
        helpCenterSessionId,
        accessChannel: typeof access_channel === 'string' ? access_channel : null,
      }),
    )
  }, [access_channel, track, helpCenterSessionId])

  const {
    fetch: fetchRecentTransactions,
    transformedData: recentTransactions,
    isLoading: isTransactionsLoading,
  } = useFetch(getRecentTransactions, transformHelpCenterTransactionsResponse)

  const {
    fetch: fetchFaqs,
    data: faqs,
    transformedData: faqEntries,
    isLoading: isFaqsLoading,
  } = useFetch(getFaqEntries, transformHelpCenterHomeFaqsResponse)

  const {
    fetch: fetchFaqEntryById,
    transformedData: feedbackFaqEntry,
    isLoading: isFeedbackFaqEntryLoading,
  } = useFetch(getFaqEntryById, transformFaqEntryResponse)

  useEffect(() => {
    Promise.all([
      fetchRecentTransactions(HC_HOME_VISIBLE_TRANSACTION_COUNT),
      fetchFaqs(),
      fetchFaqEntryById(FaqEntryType.MembersFeedback.toString()),
    ])
  }, [fetchFaqs, fetchRecentTransactions, fetchFaqEntryById])

  const renderSearch = () => {
    if (isWebview) return null

    return (
      <>
        <HelpCenterSearchInput />
        <Spacer size={Spacer.Size.Medium} />
      </>
    )
  }

  const renderNavigation = () => {
    if (!faqs) return null

    const navigationFaqEntries = transformHelpCenterNavigationResponse(faqs)

    return (
      <HelpCenterNavigation navigationFaqEntries={navigationFaqEntries} isLoading={isFaqsLoading} />
    )
  }

  const renderTransactions = () => {
    if (!recentTransactions?.transactions.length) return null

    return (
      <>
        <Cell>
          <Text as="h1" type={Text.Type.Heading} width={Text.Width.Parent}>
            {translate('transactions.title')}
          </Text>
          <Text as="h3" type={Text.Type.Subtitle}>
            {translate('transactions.subtitle')}
          </Text>
        </Cell>
        <HelpCenterTransactions
          transactions={recentTransactions.transactions}
          hasMoreTransactions={recentTransactions.totalCount > HC_HOME_VISIBLE_TRANSACTION_COUNT}
        />
      </>
    )
  }

  const renderIcon = (icon: string | undefined, title: string) => {
    if (!icon) return null

    return (
      <Image
        src={asset(`${icon}.svg`, { theme: { dark: `${icon}_dark.svg` } })}
        size={Image.Size.Large}
        alt={title}
      />
    )
  }

  const renderEntry = ({ id, title, icon }: HelpCenterHomeFaqEntryModel) => {
    if (!title) return null

    return (
      <a
        key={id}
        href={generateFaqLink({ id, title }, access_channel, thread_id)}
        className="faq__category"
        title={title}
        data-testid={`help-center-home--faq-category-${id}`}
      >
        {renderIcon(icon, title)}
        <div className="faq__category-title">{title}</div>
      </a>
    )
  }

  const renderFeedbackFormLink = () => {
    if (!isProductFeedbackFormEnabled || !isLoggedIn) {
      return null
    }

    if (isFeedbackFaqEntryLoading || !feedbackFaqEntry) {
      return <ContentLoader />
    }

    const feedbackFaqEntryUrl = generateFaqLink(
      {
        id: feedbackFaqEntry.id,
        title: feedbackFaqEntry.title,
      },
      access_channel,
      thread_id,
    )

    return (
      <>
        <Spacer size={Spacer.Size.Medium} />
        <Cell
          testId="faq-feedback-entry"
          url={feedbackFaqEntryUrl}
          type={Cell.Type.Navigating}
          title={translate('faq.give_us_feedback')}
          chevron
        />
      </>
    )
  }

  const renderFaqEntries = () => {
    if (!faqEntries) return null

    return (
      <Card>
        <div className="faq__categories" role="list">
          {faqEntries.map(renderEntry)}
        </div>
      </Card>
    )
  }

  if (isTransactionsLoading || isFaqsLoading || !isClientSideRenderReady) {
    return (
      <ContentLoader
        styling={ContentLoader.Styling.Wide}
        size={ContentLoader.Size.Large}
        testId="help-center-home-loader"
      />
    )
  }

  return (
    <>
      <aside className="body-content__sidebar faq__navigation faq__navigation--secondary">
        {renderNavigation()}
      </aside>
      <div className="faq">
        {renderSearch()}
        {renderTransactions()}
        <Cell>
          <Text as="h2" type={Text.Type.Heading} text={translate('faq.general_topics')} />
        </Cell>
        {renderFaqEntries()}
        {renderFeedbackFormLink()}
      </div>
    </>
  )
}

export default HelpCenterHome
